import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { Poi, PoiCategory } from '@/types/api';

export const poisKey = ['pois'] as const;

// Public list of live POIs. Mutations below invalidate this key so add / edit /
// delete reflect on the map without a manual refetch.
export function usePois() {
  return useQuery({
    queryKey: poisKey,
    queryFn: async () => (await api.get<{ data: Poi[] }>('/pois')).data,
  });
}

export type CreatePoiInput = {
  category: PoiCategory;
  name: string;
  comment?: string | null;
  latitude: number;
  longitude: number;
};

export function useCreatePoi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePoiInput) => api.post<{ data: Poi }>('/pois', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: poisKey });
    },
  });
}

// Partial update. Coordinates are intentionally not in the payload — position is
// fixed at create (server enforces; the form omits them).
export type UpdatePoiInput = {
  category?: PoiCategory;
  name?: string;
  comment?: string | null;
};

export function useUpdatePoi(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePoiInput) => api.patch<{ data: Poi }>(`/pois/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: poisKey });
    },
  });
}

export function useDeletePoi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/pois/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: poisKey });
    },
  });
}
